import { queriesPayStatements } from "@query";
import {
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";

import { selectorPaystubSummary } from "../../selectors/selectorPaystubSummary";

import { TablePaystubs } from "./TablePaystubs";

type Props = {
  payrollRunId?: string;
  onRowOpen: (id: string) => void;
  onDownload?: () => void;
};

export const FilterableTablePaystubs: React.FC<Props> = props => {
  // Whole-list read (useQueryAll): the paystub list has no useful server sort,
  // and the screen needs the full set for the count and bulk selection.
  // Bounded per run via the payrollRunId filter. No client-side filter/sort
  // layered on.
  const query = queriesPayStatements.listPayStatements.useQueryAll(
    { filter: { payrollRunId: { eq: props.payrollRunId ?? "" } } },
    { enabled: !!props.payrollRunId }
  );

  const paystubs = (query.flatData ?? []).map(selectorPaystubSummary);

  if (query.isError) {
    return (
      <WSEmptyState
        actions={{
          buttons: [{ label: "Try again", onClick: () => query.refetch() }]
        }}
        description="Something went wrong loading the paystubs for this run."
        title="Couldn't load paystubs"
        type="error"
      />
    );
  }

  if (!query.isInitialLoading && paystubs.length === 0) {
    return (
      <WSEmptyState
        description="Paystubs will appear here as they are processed."
        title="No paystubs yet"
        type="documents"
      />
    );
  }

  return (
    <WSList gap="S">
      <WSFlexBox.CenterY justify="flex-end">
        <WSText.ParagraphXs color="gray500" mr="S">
          Showing
        </WSText.ParagraphXs>
        <WSPill text={`${paystubs.length} items`} theme="neutral" />
      </WSFlexBox.CenterY>

      <TablePaystubs
        items={paystubs}
        loading={query.isInitialLoading}
        onRowOpen={props.onRowOpen}
      />
    </WSList>
  );
};
